import { useUserBlocker } from '@/hooks/use-user-blocker';
import { UserBlocker } from '@/hooks/use-user-blocker/types';
import { MediaGuideMetadata } from '@/utils/media/types';
import { EventProperties } from '@calm/evexia-analytics';
import {
    MediaUpdateGuideProgressActionEnum,
    WorkflowStepInfo,
} from '@calm/evexia-web-sdk';
import { AudioPlayerOpenState } from '@rhg/components/media/audio-player-combined/types';
import { AUDIO_PLAYER_DRAWER_HEIGHT } from '@rhg/components/media/audio-player-drawer';
import { ControlButtonType } from '@rhg/components/media/video-player/types';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useAudio } from '@rhg/hooks/use-audio';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useGuideQueue } from '@rhg/hooks/use-guide-queue';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useRecentAndFavoriteGuides } from '@rhg/hooks/use-recent-and-favorite-guides';
import { useUpdateGuide } from '@rhg/hooks/use-update-guide';
import { getLocalDateTimeString } from '@rhg/utils/date-time';
import { getGuideId, MediaEvent } from '@rhg/utils/media';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ActiveProgramProvider } from '../program';
import { GuidePlayerActiveSession } from './active-player';
import { createGuidePlayerEventBus } from './events';
import { JournalingTransitionOverlay } from './journaling-transition-overlay';
import {
    PROGRAM_QUEUE_SOURCE,
    queueSourceKey,
    queueSourceTrackProps,
} from './queue-source';
import {
    GuidePlayerState,
    GuideProgressSource,
    OpenGuidePlayerProps,
    PlayerMode,
    QueueSource,
} from './types';

const GuideCompletedDialogs = dynamic(() =>
    import('@rhg/components/guide/completed-dialogs').then(
        (mod) => mod.GuideCompletedDialogs
    )
);

export enum PlaybackMode {
    Soundscape,
    Playlist,
    Normal,
}

export enum TrackToPlay {
    Previous,
    Next,
}

export const GuidePlayerContext = createContext<GuidePlayerState>(null!);

function usePlayerHeight(isOpen: boolean) {
    const currentBreakpoint = useCurrentBreakpointName();
    if (!isOpen) {
        return 0;
    }

    return AUDIO_PLAYER_DRAWER_HEIGHT[currentBreakpoint];
}

interface Props {
    children: ReactNode;
}

export const GUIDE_PLAYER_OPEN_STATE_KEY = 'guide_player_open_state';

// Short delay before switching the dialog to drawer when the user clicks a
// navigation link. Lets the link click commit to a route transition before
// the player visibly collapses, avoiding a brief flash of the drawer.
const MINIMIZE_DELAY_MS = 300;

const OPEN_STATE_TO_PLAYER_MODE: Record<
    Exclude<AudioPlayerOpenState, false>,
    PlayerMode
> = {
    dialog: PlayerMode.Dialog,
    drawer: PlayerMode.Drawer,
};

export const GuidePlayerProvider = ({ children }: Props) => {
    const { activeCircle, activeCircleId } = useActiveCircle();
    const { unloadAudio } = useAudio();
    const { userBlocker } = useUserBlocker();
    const { updateGuideAsync } = useUpdateGuide();
    const { refresh: refreshRecentsAndFavorites } = useRecentAndFavoriteGuides(
        activeCircleId,
        false
    );
    const { resetLoop, resetShuffle } = useGuideQueue();
    const router = useRouter();

    const [isOpen, setIsOpen] = useState(false);
    const [playerMode, setPlayerMode] = useState<PlayerMode>(PlayerMode.Dialog);
    const [isGuideCompletedOpen, setIsGuideCompletedOpen] = useState(false);
    const [progressSource, setProgressSource] = useState<
        GuideProgressSource | undefined
    >();
    const eventBus = useRef(createGuidePlayerEventBus());
    const playingGuideIdRef = useRef<string | undefined>(undefined);
    const [completedContentStep, setCompletedContentStep] = useState<
        WorkflowStepInfo | undefined
    >(undefined);
    const [playingGuide, setPlayingGuide] = useState<MediaGuideMetadata | null>(
        null
    );
    const [trackFn, setTrackFn] = useState<MediaTrackerFn | undefined>(
        undefined
    );
    const [willSkipToPreviousProgress, setWillSkipToPreviousProgress] =
        useState(false);
    const [willShuffle, setWillShuffle] = useState(false);
    const [sessionTrackProps, setSessionTrackProps] = useState<EventProperties>(
        {}
    );
    const [programContextProps, setProgramContextProps] = useState<
        | {
              programId: string;
              packClass?: string;
              sourceKey: string;
              queueSource: QueueSource;
          }
        | undefined
    >();
    const [isStartingJournaling, setIsStartingJournaling] = useState(false);

    // Clear the journaling transition overlay once navigation to the entry
    // page completes — the destination page's own JournalingLoadingScreen
    // takes over from there until workflow data resolves.
    useEffect(() => {
        const handleRouteChangeComplete = () => setIsStartingJournaling(false);
        router.events.on('routeChangeComplete', handleRouteChangeComplete);
        return () => {
            router.events.off('routeChangeComplete', handleRouteChangeComplete);
        };
    }, [router.events]);

    // Computed for external consumers and backwards compat
    const openState: AudioPlayerOpenState = isOpen ? playerMode : false;

    const playerHeight = usePlayerHeight(isOpen);
    const playingGuideId = playingGuide ? getGuideId(playingGuide) : undefined;
    playingGuideIdRef.current = playingGuideId;

    const closeGuidePlayer = useCallback(
        (
            isTriggeredFromTrackChange = false,
            {
                keepShuffle = false,
                isSwitchingGuide = false,
            }: { keepShuffle?: boolean; isSwitchingGuide?: boolean } = {}
        ) => {
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(GUIDE_PLAYER_OPEN_STATE_KEY);
            if (!isTriggeredFromTrackChange) {
                setIsOpen(false);
            }
            setTrackFn(() => undefined);
            if (!isTriggeredFromTrackChange) {
                setPlayingGuide(null);
                setProgressSource(undefined);
                if (playingGuideIdRef.current && !isSwitchingGuide) {
                    eventBus.current.emit({
                        type: 'closed',
                        guideId: playingGuideIdRef.current,
                    });
                }
            }
            unloadAudio();
            if (!isTriggeredFromTrackChange) {
                setProgramContextProps(undefined);
            }
            // A genuine user close disables shuffle so the next open plays in
            // order. openGuidePlayer's internal teardown passes keepShuffle so a
            // next/previous track switch (which reopens the player on a new
            // guide) does NOT wipe the active shuffle order (EVE-11935).
            if (!isTriggeredFromTrackChange && !keepShuffle) {
                resetShuffle();
            }
            setIsGuideCompletedOpen(false);
            setCompletedContentStep(undefined);
        },
        [unloadAudio, resetShuffle]
    );

    const openGuidePlayer = useCallback(
        async ({
            openState: requestedOpenState,
            guide,
            packClass,
            workflow,
            trackFn,
            willSkipToPreviousProgress,
            sessionTrackProps,
            willShuffle: requestedWillShuffle,
            queueSource: requestedQueueSource,
            progressSource: requestedProgressSource,
        }: OpenGuidePlayerProps) => {
            const guideId = getGuideId(guide);
            const isUnsponsored = userBlocker === UserBlocker.Unsponsored;
            const newGuideIsCurrentlyPlaying = guideId === playingGuideId;
            if (!newGuideIsCurrentlyPlaying) {
                // Internal teardown before loading the new guide — keep shuffle
                // so a track change (next/previous) doesn't reset it. A new
                // album is handled by setQueue's new-context branch instead.
                // Not a user close, so it doesn't emit `closed` for the old guide.
                closeGuidePlayer(false, {
                    keepShuffle: true,
                    isSwitchingGuide: true,
                });
            }

            const nextSource = requestedQueueSource ?? PROGRAM_QUEUE_SOURCE;
            const nextSourceKey = queueSourceKey(nextSource, guide);
            if (nextSourceKey !== programContextProps?.sourceKey) {
                resetLoop();
            }
            setProgramContextProps({
                packClass,
                programId: guide.programId,
                sourceKey: nextSourceKey,
                queueSource: nextSource,
            });
            setTrackFn(() => trackFn);
            setSessionTrackProps(sessionTrackProps ?? {});
            setProgressSource(requestedProgressSource);
            setPlayingGuide(guide);
            setWillSkipToPreviousProgress(willSkipToPreviousProgress ?? false);
            setWillShuffle(requestedWillShuffle ?? false);

            const newMode: PlayerMode =
                requestedOpenState === 'drawer'
                    ? PlayerMode.Drawer
                    : PlayerMode.Dialog;
            setPlayerMode(newMode);
            setIsOpen(true);
            // eslint-disable-next-line no-restricted-syntax
            localStorage.setItem(GUIDE_PLAYER_OPEN_STATE_KEY, newMode);

            if (!newGuideIsCurrentlyPlaying && !isUnsponsored) {
                await updateGuideAsync({
                    guideId,
                    workflowDefnId: workflow?.workflowDefn?.defnId,
                    workflowType: workflow?.workflowDefn?.type,
                    request: {
                        progressDurationMs: 0,
                        timestamp: getLocalDateTimeString(),
                        progressAction:
                            MediaUpdateGuideProgressActionEnum.Start,
                    },
                });
            }
            refreshRecentsAndFavorites();
        },
        [
            userBlocker,
            playingGuideId,
            programContextProps,
            refreshRecentsAndFavorites,
            closeGuidePlayer,
            updateGuideAsync,
            resetLoop,
        ]
    );

    // Adapter for GuidePlayer — maps AudioPlayerOpenState to the split isOpen/playerMode state.
    // setOpenState(false) closes without changing playerMode, preserving the last mode.
    const setOpenState = useCallback((state: AudioPlayerOpenState) => {
        setIsOpen(!!state);
        if (state) setPlayerMode(OPEN_STATE_TO_PLAYER_MODE[state]);
    }, []);

    const onMinimize = useCallback(() => {
        // setPlayerMode-only: dialog stays mounted via the same provider, so
        // openState already reflects 'dialog'. Switching to drawer is purely a
        // display-mode change; openState is updated by setOpenState callers
        // (e.g. minimize button via OPEN_STATE_TO_PLAYER_MODE).
        setPlayerMode(PlayerMode.Drawer);
    }, []);

    const navigationMinimizeTimeoutRef = useRef<
        number | NodeJS.Timeout | undefined
    >(undefined);

    useEffect(() => {
        return () => {
            if (navigationMinimizeTimeoutRef.current) {
                clearTimeout(navigationMinimizeTimeoutRef.current);
            }
        };
    }, []);

    const minimizeForNavigation = useCallback(() => {
        // Wait a beat before minimizing so the player doesn't briefly flash to
        // drawer before the link click navigates away.
        if (navigationMinimizeTimeoutRef.current) {
            clearTimeout(navigationMinimizeTimeoutRef.current);
        }
        navigationMinimizeTimeoutRef.current = setTimeout(() => {
            // setPlayerMode-only (not setOpenState): user is navigating away.
            // The page unmounts the dialog, so openState churn is moot — only
            // the next presentation needs to be a drawer when the player
            // rehydrates.
            setPlayerMode(PlayerMode.Drawer);
        }, MINIMIZE_DELAY_MS);
    }, []);

    const handleTrack = useCallback<MediaTrackerFn>(
        (event) => {
            trackFn?.(event);
            if (
                event.eventType === MediaEvent.Ended &&
                playingGuideIdRef.current
            ) {
                eventBus.current.emit({
                    type: 'ended',
                    guideId: playingGuideIdRef.current,
                });
            }
        },
        [trackFn]
    );
    const subscribe = useCallback(
        (listener: Parameters<typeof eventBus.current.subscribe>[0]) =>
            eventBus.current.subscribe(listener),
        []
    );

    const queueSource = programContextProps?.queueSource;
    const queueTrackProps = useMemo(
        () => queueSourceTrackProps(queueSource),
        [queueSource]
    );

    const playbackToggleTracker = useRef<
        ((buttonType: ControlButtonType) => void) | undefined
    >(undefined);
    const registerPlaybackToggleTracker = useCallback(
        (tracker: ((buttonType: ControlButtonType) => void) | undefined) => {
            playbackToggleTracker.current = tracker;
        },
        []
    );
    const trackPlaybackToggle = useCallback(
        (buttonType: ControlButtonType) =>
            playbackToggleTracker.current?.(buttonType),
        []
    );

    const guidePlayerValue = useMemo(
        () => ({
            closeGuidePlayer,
            minimizeForNavigation,
            onMinimize,
            openGuidePlayer,
            openState,
            playerHeight,
            playingGuide,
            queueSource,
            progressSource,
            registerPlaybackToggleTracker,
            subscribe,
            trackPlaybackToggle,
        }),
        [
            closeGuidePlayer,
            minimizeForNavigation,
            onMinimize,
            openGuidePlayer,
            openState,
            playerHeight,
            playingGuide,
            queueSource,
            progressSource,
            registerPlaybackToggleTracker,
            subscribe,
            trackPlaybackToggle,
        ]
    );

    return (
        <GuidePlayerContext.Provider value={guidePlayerValue}>
            {children}
            {(isGuideCompletedOpen || isOpen) &&
                playingGuide &&
                playingGuideId && (
                    <ActiveProgramProvider
                        programId={programContextProps?.programId}
                        packClass={programContextProps?.packClass}
                    >
                        {isGuideCompletedOpen && (
                            <GuideCompletedDialogs
                                isOpen={isGuideCompletedOpen}
                                onClose={closeGuidePlayer}
                                openGuidePlayer={openGuidePlayer}
                                guideId={playingGuideId}
                                completedContentStep={completedContentStep}
                                playerMode={playerMode}
                            />
                        )}
                        {isOpen && (
                            <GuidePlayerActiveSession
                                isOpen={isOpen}
                                playerMode={playerMode}
                                setOpenState={setOpenState}
                                playingGuide={playingGuide}
                                playingGuideId={playingGuideId}
                                trackFn={handleTrack}
                                willSkipToPreviousProgress={
                                    willSkipToPreviousProgress
                                }
                                sessionTrackProps={sessionTrackProps}
                                queueSource={queueSource}
                                queueTrackProps={queueTrackProps}
                                setIsGuideCompletedOpen={
                                    setIsGuideCompletedOpen
                                }
                                setCompletedContentStep={
                                    setCompletedContentStep
                                }
                                closeGuidePlayer={closeGuidePlayer}
                                openGuidePlayer={openGuidePlayer}
                                onMinimize={onMinimize}
                                minimizeForNavigation={minimizeForNavigation}
                                activeCircle={activeCircle}
                                activeCircleId={activeCircleId}
                                willShuffle={willShuffle}
                                setIsStartingJournaling={
                                    setIsStartingJournaling
                                }
                            />
                        )}
                    </ActiveProgramProvider>
                )}
            <JournalingTransitionOverlay isOpen={isStartingJournaling} />
        </GuidePlayerContext.Provider>
    );
};
